/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  useCallback,
  useState
} from 'react';
import {useDispatch} from 'react-redux';
import {makeStyles, Tooltip} from '@material-ui/core';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {getGroup} from '@ion/core/src/services/Connect';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {Link} from '@ion/core/src/types/entities';
import ClickAwayListener from '@ion/dls/src/components/listeners/ClickAwayListener';
import {GridResult} from '@ion/dls/src/grid/PaginationControls';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {handleColumnError} from '../../utils/errorMessages';
import {viewGroup} from '../admin/groups/state';
import DeviceGroupTitle from './DeviceGroupTitle';

export interface DeviceGroupLinksType {
  data: GridResult;
}

const useStyles = makeStyles(() => ({
  groupSection : {
    display : 'flex'
  },
  groupSections : {
    display       : 'flex',
    alignItems    : 'center',
    letterSpacing : '0.4px'
  },
  groupName : {
    cursor         : 'pointer',
    textDecoration : 'underline'
  },
  moreGroups : {
    color : dlsColor('grey3')
  },
  descriptionIcon : {
    marginRight : '8px'
  }
}));

const groupTooltip = makeStyles(() => ({
  arrow : {
    color : dlsColor('grey1')
  },
  tooltip : {
    ...dlsAlt('components', 'reportTooltip')
  },
  popper : {
    width         : 'auto',
    pointerEvents : 'auto'
  }
}));

/**
 * Defines the DeviceGroupLinks component. This component is used to
 * display the group names with links when we hover on groups column
 *
 * ```typescript
 *
 *   import DeviceGroupLinks from './DeviceGroupLinks';
 *
 *   <IonGrid  columnComponents={{
 *     groupsUI : DeviceGroupLinks
 *   }} />
 *
 * ```
 */
export const DeviceGroupLinks = ({data}: DeviceGroupLinksType) => {
  const dispatch                    = useDispatch();
  const classes                     = useStyles();
  const groups                      = data?.groups;
  const groupLength                 = groups?.length || 0;
  const moreGroupsText              = groupLength > 1 ? `${translate('admin-devices:grid.moreGroups', {groupLength: groupLength - 1})}` : '';
  const [openGroups, setOpenGroups] = useState(false);

  const toggleTooltip = useCallback((open = false) => {
    return function(e) {
      e.preventDefault();
      if (!open) {
        setOpenGroups(false);
      } else {
        setOpenGroups(true);
      }
    };
  }, [openGroups]);

  const onGroupClick = useCallback((group: Link) => {
    toggleTooltip();
    (async function() {
      try {
        const groupData = await getGroup(entityId(group?.href));
        dispatch(viewGroup(groupData));
      } catch (err) {
        error(err);
        dispatch(handleColumnError('Entity', 'Group'));
      }
    })();
  }, []);

  const onFirstGroupClick = useCallback(() => {
    onGroupClick(groups[0]);
  }, []);

  if (data) {
    return (
      <>
        <ClickAwayListener onClickAway={toggleTooltip()}
          mountListener>
          <Tooltip arrow
            classes={groupTooltip()}
            open={openGroups}
            onClose={toggleTooltip()}
            interactive={true}
            title={
              <DeviceGroupTitle groups={groups}
                onGroupClick={onGroupClick} />
            }
            placement='top'>
            <div className={classes.groupSection}>
              <div className={classes.groupName}
                onClick={onFirstGroupClick}>
                {groups?.[0]?.title}
              </div>
              {groupLength > 1 && (
                <div className={classes.moreGroups}
                  onClick={toggleTooltip(true)}>
                    ,&nbsp;<span className={classes.groupName}>{moreGroupsText}</span>
                </div>
              )}
            </div>
          </Tooltip>
        </ClickAwayListener>
      </>
    );
  }
  return <div/>;
};

export default DeviceGroupLinks;
